import React, { useCallback, useState } from 'react';
import DownloadIcon from '@moda/icons/download-24';
import { Text, Stack, Clickable, Loading } from '@moda/om';
import FileSaver from 'file-saver';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { usePurchaseOrderPdf } from '../../../api/purchaseOrders';
import { ErrorPage } from '../../../pages/ErrorPage/ErrorPage';
import { PURCHASE_ORDERS } from '../../../api/paths';
import { reportError } from '../../../lib/reportError';

import './PurchaseOrderDetailPanel.scss';

const options = {
  cMapUrl: 'cmaps/',
  cMapPacked: true,
  withCredentials: true
};

interface Props {
  poNumber: string;
}

export const PurchaseOrderDetailPanel: React.FC<Props> = ({ poNumber }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const { data: pdf, error, isLoading: loading } = usePurchaseOrderPdf(poNumber);

  const [isDocumentReady, setIsDocumentReady] = useState(false);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsDocumentReady(true);
  };

  const handleDownloadPdf = useCallback(() => {
    if (!pdf) return;
    FileSaver.saveAs(pdf, `${poNumber}.pdf`);
  }, [pdf, poNumber]);

  if (loading) return <Loading />;
  if (error) return <ErrorPage />;

  return (
    <div className="PurchaseOrderDetailPanel">
      <Stack
        space={1}
        direction="horizontal"
        justifyContent="space-between"
        className="PurchaseOrderDetailPanel__header"
      >
        <div className="PurchaseOrderDetailPanel__po-number">
          <Text treatment="h3" color="fog">
            PO #{poNumber}
          </Text>
        </div>

        <Clickable className="PurchaseOrderDetailPanel__download-text" onClick={handleDownloadPdf}>
          <Stack
            space={1}
            direction="horizontal"
            alignItems="center"
            className="PurchaseOrderDetailPanel__download-link"
          >
            <DownloadIcon /> Download
          </Stack>
        </Clickable>
      </Stack>
      <Document
        file={pdf}
        key={`${PURCHASE_ORDERS}/${poNumber}.pdf`}
        loading={<Loading />}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={error => {
          reportError('PURCHASE_ORDER_PDF_LOAD', error);
        }}
        options={options}
      >
        {isDocumentReady &&
          Array.from(new Array(numPages), (el, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              className="PurchaseOrderDetailPanel__pdf-page"
            />
          ))}
      </Document>
    </div>
  );
};
